import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@helpers/api';
import { useCommunity } from 'contexts/community';
import { DATE_INTERVAL_PRESETS, toApiDate } from '../enums/dateIntervalPresets';
import { CHARGE_STATUS_MAP } from '../enums/chargeStatuses';
import { PAYMENT_METHOD_MAP } from '../enums/paymentMethods';
import { GRANULARITY_TYPE_MAP } from '../enums/granularityTypes';

const INITIAL_PRESET = DATE_INTERVAL_PRESETS.find(p => p.id === 'last_30_days');
const INITIAL_RANGE = INITIAL_PRESET.resolve();
const DEFAULT_GRANULARITY = { type: 'day', amount: 1 };

export default function useInsights() {
    const { communities, loading: communitiesLoading } = useCommunity();
    const [searchParams, setSearchParams] = useSearchParams();

    const ownedCommunities = useMemo(
        () => (communities || []).filter(c => c.role === 'owner'),
        [communities]
    );

    // Parse URL filters once on first render. We intentionally don't react to
    // later URL changes — the URL is one-way derived from filter state after
    // mount so the page can be shared via link.
    const initialFromUrlRef = useRef(null);
    if (initialFromUrlRef.current === null) {
        initialFromUrlRef.current = parseFiltersFromUrl(searchParams);
    }
    const initialFromUrl = initialFromUrlRef.current;

    const [community, setCommunityState] = useState(initialFromUrl.community);
    const [dateRange, setDateRange] = useState(initialFromUrl.dateRange || {
        presetId: INITIAL_PRESET.id,
        from: INITIAL_RANGE.from,
        to: INITIAL_RANGE.to,
    });
    const [planIds, setPlanIds] = useState(initialFromUrl.planIds);
    const [chargeStatuses, setChargeStatuses] = useState(initialFromUrl.chargeStatuses);
    const [chargeMethods, setChargeMethods] = useState(initialFromUrl.chargeMethods);
    const [granularity, setGranularity] = useState(initialFromUrl.granularity || DEFAULT_GRANULARITY);

    const [plansByCommunity, setPlansByCommunity] = useState({});
    const [plansLoading, setPlansLoading] = useState(false);

    const plans = useMemo(
        () => (community ? plansByCommunity[community] || [] : []),
        [community, plansByCommunity]
    );

    const [report, setReport] = useState(null);
    const [reportLoading, setReportLoading] = useState(false);
    const reportReqIdRef = useRef(0);
    // Ref-backed mirror of plansLoading. React batches setState so reading the
    // state value from within an effect that runs in the same render cycle as
    // loadPlans() sees the stale value. The ref updates synchronously.
    const plansLoadingRef = useRef(false);
    // Gate URL writes and the "default plan ids = all plans" behavior until
    // the first plans-load has finished, so URL-supplied planIds aren't wiped.
    const initialPlansLoadedRef = useRef(false);

    useEffect(() => {
        if (!ownedCommunities.length) return;
        loadAllPlans(ownedCommunities);
    }, [ownedCommunities]);

    useEffect(() => {
        if (!ownedCommunities.length) return;

        // Drop a URL-supplied community that doesn't belong to this user.
        if (community && !ownedCommunities.some(c => c.id === community)) {
            setCommunityState(null);
            return;
        }
        if (community) return;

        const firstWithPlans = ownedCommunities.find(c => (plansByCommunity[c.id] || []).length > 0);
        if (firstWithPlans) {
            setCommunityState(firstWithPlans.id);
        } else if (!plansLoadingRef.current) {
            setCommunityState(ownedCommunities[0].id);
        }
    }, [ownedCommunities, community, plansByCommunity]);

    useEffect(() => {
        if (plansLoading) return;
        if (!community) return;
        if (initialPlansLoadedRef.current) return;
        initialPlansLoadedRef.current = true;

        // First plans-load for the URL-initialized community: keep the URL's
        // planIds (filtered to ones that still exist) or fall back to all.
        // If the community has no plans we leave planIds as the URL value —
        // insights aren't shown for plan-less communities anyway.
        if (!plans.length) return;
        if (initialFromUrl.planIds.length) {
            const valid = initialFromUrl.planIds.filter(id => plans.some(p => p.id === id));
            setPlanIds(valid.length ? valid : plans.map(p => p.id));
        } else {
            setPlanIds(plans.map(p => p.id));
        }
    }, [plansLoading, community, plans]);

    useEffect(() => {
        if (!community || !dateRange?.from || !dateRange?.to) return;
        if (plansLoadingRef.current) return;
        if (!plans.length) return;
        loadReport();
    }, [community, dateRange, planIds, chargeStatuses, chargeMethods, granularity, plansLoading]);

    // Sync filter state → URL. Runs only after the initial plans load so the
    // URL-supplied planIds get a chance to be applied first.
    useEffect(() => {
        if (!initialPlansLoadedRef.current) return;
        setSearchParams(
            serializeFiltersToUrl({
                community,
                dateRange,
                planIds,
                allPlanIds: plans.map(p => p.id),
                chargeStatuses,
                chargeMethods,
                granularity,
            }),
            { replace: true }
        );
    }, [community, dateRange, planIds, chargeStatuses, chargeMethods, granularity, plans]);

    function setCommunity(newCommunity) {
        setCommunityState(newCommunity);
        const newPlans = plansByCommunity[newCommunity] || [];
        setPlanIds(newPlans.map(p => p.id));
    }

    async function loadAllPlans(communities) {
        plansLoadingRef.current = true;
        setPlansLoading(true);
        try {
            const results = await Promise.all(
                communities.map(c =>
                    api.get(`community/${c.id}/plan`, { include_deleted: true })
                        .then(({ data }) => [c.id, Array.isArray(data) ? sortPlans(data) : []])
                        .catch(() => [c.id, []])
                )
            );
            setPlansByCommunity(Object.fromEntries(results));
        } catch (e) {
            console.log(e);
            setPlansByCommunity({});
        }
        plansLoadingRef.current = false;
        setPlansLoading(false);
    }

    function sortPlans(list) {
        return [...list].sort((a, b) => {
            const aDeleted = !!a.deleted_at;
            const bDeleted = !!b.deleted_at;
            if (aDeleted !== bDeleted) return aDeleted ? 1 : -1;
            return (a.label || '').localeCompare(b.label || '');
        });
    }

    async function loadReport() {
        const reqId = ++reportReqIdRef.current;
        setReportLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('date_from', toApiDate(dateRange.from));
            params.append('date_to', toApiDate(dateRange.to));
            params.append('granularity_type', granularity.type);
            params.append('granularity_amount', granularity.amount);
            const planIdsToSend = planIds.length ? planIds : plans.map(p => p.id);
            planIdsToSend.forEach(id => params.append('plan_ids[]', id));
            chargeStatuses.forEach(s => params.append('charge_statuses[]', s));
            chargeMethods.forEach(m => params.append('charge_methods[]', m));

            const { data } = await api.get(`community/${community}/insights/salesReport?${params.toString()}`);
            if (reqId !== reportReqIdRef.current) return;
            setReport(data || null);
        } catch (e) {
            if (reqId !== reportReqIdRef.current) return;
            console.log(e);
            setReport(null);
        }
        if (reqId === reportReqIdRef.current) {
            setReportLoading(false);
        }
    }

    function applyPreset(presetId) {
        const preset = DATE_INTERVAL_PRESETS.find(p => p.id === presetId);
        if (!preset) return;
        const { from, to } = preset.resolve();
        setDateRange({ presetId, from, to });
    }

    function applyCustomRange(from, to) {
        if (!from || !to) return;
        setDateRange({ presetId: 'custom', from, to });
    }

    function clearOptionalFilters() {
        setPlanIds(plans.map(p => p.id));
        setChargeStatuses([]);
        setChargeMethods([]);
    }

    return {
        communitiesLoading,
        ownedCommunities,
        community,
        setCommunity,

        dateRange,
        applyPreset,
        applyCustomRange,

        plans,
        plansByCommunity,
        plansLoading,
        planIds,
        setPlanIds,

        chargeStatuses,
        setChargeStatuses,

        chargeMethods,
        setChargeMethods,

        granularity,
        setGranularity,

        clearOptionalFilters,

        report,
        reportLoading,
    };
}

function parseFiltersFromUrl(searchParams) {
    return {
        community: parseCommunity(searchParams.get('community')),
        dateRange: parseDateRange(searchParams),
        planIds: parseIdList(searchParams.get('plans'), true),
        chargeStatuses: parseIdList(searchParams.get('statuses'), false)
            .filter(id => CHARGE_STATUS_MAP[id]),
        chargeMethods: parseIdList(searchParams.get('methods'), false)
            .filter(id => PAYMENT_METHOD_MAP[id]),
        granularity: parseGranularity(searchParams.get('granularity')),
    };
}

function parseCommunity(raw) {
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
}

function parseDateRange(searchParams) {
    const presetId = searchParams.get('preset');
    if (!presetId) return null;
    if (presetId === 'custom') {
        const from = parseDateOnly(searchParams.get('from'), false);
        const to = parseDateOnly(searchParams.get('to'), true);
        if (!from || !to) return null;
        return { presetId: 'custom', from, to };
    }
    const preset = DATE_INTERVAL_PRESETS.find(p => p.id === presetId);
    if (!preset) return null;
    const { from, to } = preset.resolve();
    return { presetId, from, to };
}

function parseGranularity(raw) {
    if (!raw) return null;
    const [type, amountStr] = raw.split(':');
    if (!GRANULARITY_TYPE_MAP[type]) return null;
    const amount = amountStr ? parseInt(amountStr, 10) : 1;
    if (Number.isNaN(amount) || amount < 1) return null;
    return { type, amount };
}

function parseIdList(raw, numeric) {
    if (!raw) return [];
    return raw.split(',')
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => numeric ? parseInt(s, 10) : s)
        .filter(v => !numeric || !Number.isNaN(v));
}

function parseDateOnly(str, isEnd) {
    if (!str) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) return null;
    const [, y, mo, d] = m;
    const date = new Date(+y, +mo - 1, +d, isEnd ? 23 : 0, isEnd ? 59 : 0, isEnd ? 59 : 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
}

function toDateOnly(d) {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function serializeFiltersToUrl({
    community,
    dateRange,
    planIds,
    allPlanIds,
    chargeStatuses,
    chargeMethods,
    granularity,
}) {
    const params = {};

    if (community != null) params.community = String(community);

    if (dateRange?.presetId && dateRange.presetId !== INITIAL_PRESET.id) {
        params.preset = dateRange.presetId;
        if (dateRange.presetId === 'custom') {
            params.from = toDateOnly(dateRange.from);
            params.to = toDateOnly(dateRange.to);
        }
    }

    // Only include plans when the selection isn't the implicit "all" default.
    if (allPlanIds.length && !sameIdSet(planIds, allPlanIds)) {
        params.plans = planIds.join(',');
    }

    if (chargeStatuses.length) params.statuses = chargeStatuses.join(',');
    if (chargeMethods.length) params.methods = chargeMethods.join(',');

    if (granularity.type !== DEFAULT_GRANULARITY.type || granularity.amount !== DEFAULT_GRANULARITY.amount) {
        params.granularity = granularity.amount > 1
            ? `${granularity.type}:${granularity.amount}`
            : granularity.type;
    }

    return params;
}

function sameIdSet(a, b) {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every(id => set.has(id));
}
